import {
  Button,
  IconButton,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Link,
} from '@mui/material';
import { ChangeEvent, MouseEvent, useCallback, useEffect, useState } from 'react';
import { HttpAgent } from 'src/api/agent';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Iconify from 'src/components/Iconify';
import { Link as RouterLink } from 'react-router-dom';
import Page from 'src/components/Page';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { WorkflowManagementService } from './api/WorkflowManagementService';

interface Workflow {
  code: string;
  name: string;
  description: string;
  status: string;
  type: string;
}

interface WorkflowEnums {
  statusWorkFlow: Record<string, string>;
  typeWorkFlow: Record<string, string>;
}

const workflowInitialState: Workflow = {
  code: '',
  name: '',
  description: '',
  status: '',
  type: '',
};

export default function WorkflowList() {
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [workflowInForm, setWorkflowInForm] = useState<Workflow>(workflowInitialState);
  const [editingWorkflow, setEditingWorkflow] = useState<Workflow | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [workflowEnums, setWorkflowEnums] = useState<WorkflowEnums>({ statusWorkFlow: {}, typeWorkFlow: {} });

  const fetchWorkflows = useCallback(async () => {
    try {
      setLoading(true);
      const response = await WorkflowManagementService.getWorkflows();
      setWorkflows(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Failed to fetch workflows', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWorkflowEnums = useCallback(async () => {
    try {
      const response = await WorkflowManagementService.getEnums();
      setWorkflowEnums(response);
      console.log('setWorklowEnums',response)
    } catch (error) {
      console.error('Failed to fetch workflow enums', error);
    }
  }, []);

  useEffect(() => {
    console.log('calling fetchWorkflowEnums')
    fetchWorkflows();
    fetchWorkflowEnums();
  }, [fetchWorkflows, fetchWorkflowEnums]);

  const handleOpen = () => {
    setEditingWorkflow(null);
    setWorkflowInForm(workflowInitialState);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setWorkflowInForm(workflowInitialState);
    setEditingWorkflow(null);
  };

  const handleMenuOpen = (event: MouseEvent<HTMLButtonElement>, workflow: Workflow) => {
    setAnchorEl(event.currentTarget);
    setSelectedWorkflow(workflow);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedWorkflow(null);
  };

  const handleEditClick = () => {
    if (selectedWorkflow) {
      setEditingWorkflow(selectedWorkflow);
      setWorkflowInForm(selectedWorkflow);
      setOpen(true);
    }
    handleMenuClose();
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setWorkflowInForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveWorkflow = async () => {
    if (editingWorkflow) {
      await handleUpdateWorkflow();
    } else {
      await handleCreateWorkflow();
    }
  };

  const handleCreateWorkflow = async () => {
    try {
      await WorkflowManagementService.createWorkflow(workflowInForm);
      handleClose();
      fetchWorkflows();
    } catch (error) {
      console.error('Failed to create workflow', error);
    }
  };

  const handleUpdateWorkflow = async () => {
    if (!editingWorkflow) return;
    try {
      await WorkflowManagementService.updateWorkflow(editingWorkflow.code, workflowInForm);
      handleClose();
      fetchWorkflows();
    } catch (error) {
      console.error('Failed to update workflow', error);
    }
  };

  return (
    <Page title="Workflows">
      <Container>
        <HeaderBreadcrumbs
          heading=""
          title='Workflow List'
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Workflows' },
          ]}
          action={
            <Button
              variant="contained"
              startIcon={<Iconify icon={'eva:plus-fill'} />}
              onClick={handleOpen}
            >
              New Workflow
            </Button>
          }
        />
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {workflows.map((row) => (
                  <TableRow key={row.code}>
                    <TableCell>
                      <Link component={RouterLink} to={`${PATH_DASHBOARD.crs.workflow}/${row.code}`}>
                        {row.code}
                      </Link>
                    </TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.description}</TableCell>
                    <TableCell>{row.status}</TableCell>
                    <TableCell>{row.type}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={(e) => handleMenuOpen(e, row)}>
                        <Iconify icon={'eva:more-vertical-fill'} width={20} height={20} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={handleEditClick}>
            <Iconify icon={'eva:edit-fill'} sx={{ mr: 2, width: 20, height: 20 }} />
            Edit
          </MenuItem>
        </Menu>
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
          <DialogTitle>{editingWorkflow ? 'Edit Workflow' : 'New Workflow'}</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <TextField
                name="code"
                label="Code"
                value={workflowInForm.code}
                onChange={handleInputChange}
                fullWidth
                disabled={!!editingWorkflow}
              />
              <TextField
                name="name"
                label="Name"
                value={workflowInForm.name}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                name="description"
                label="Description"
                value={workflowInForm.description}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                name="status"
                select
                label="Status"
                value={workflowInForm.status}
                onChange={handleInputChange}
                fullWidth
              >
                {Object.entries(workflowEnums?.statusWorkFlow || {}).map(([key, value]) => (
                  <MenuItem key={key} value={key}>
                    {value}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                name="type"
                select
                label="Type"
                value={workflowInForm.type}
                onChange={handleInputChange}
                fullWidth
              >
                {Object.entries(workflowEnums?.typeWorkFlow || {}).map(([key, value]) => (
                  <MenuItem key={key} value={key}>
                    {value}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={handleSaveWorkflow} variant="contained">
              {editingWorkflow ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}